/**
 * useErrorAnalysis Hook
 *
 * Provides AI-powered error analysis with caching.
 * Auto-analyzes on error; provides manual re-trigger via triggerAnalysis.
 */

import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useCallback, useEffect, useRef, useState } from "react";
import { generateCacheKey, getCachedAnalysis, removeCachedAnalysis, setCachedAnalysis, type ErrorAnalysisResult, type FixableField } from "./errorAnalysisCache";

// ---------------------------------------------------------------------------
// Sensitive field redaction (defense-in-depth — backend also redacts)
// ---------------------------------------------------------------------------

const SENSITIVE_EXACT = new Set(["apikey", "api_key", "secret", "secretkey", "secret_key", "password", "passwd", "token", "bearer", "authorization", "auth", "credential", "credentials"]);
const SENSITIVE_CONTAINS = ["privatekey", "private_key", "accesstoken", "access_token", "refreshtoken", "refresh_token", "signingkey", "signing_key", "webhooksecret", "webhook_secret", "clientsecret", "client_secret", "apisecret", "api_secret", "authtoken", "auth_token", "bearertoken", "bearer_token"];
function isSensitiveField(name: string): boolean {
  const lower = name.toLowerCase().replace(/[^a-z0-9_]/g, "");
  if (SENSITIVE_EXACT.has(lower)) return true;
  return SENSITIVE_CONTAINS.some(p => lower.includes(p));
}

/** Try to parse a JSON string, redact sensitive keys inside, re-serialize. */
function redactJSONString(s: string): string {
  const trimmed = s.trim();
  if (!trimmed.startsWith("{")) return s;
  try {
    const obj = JSON.parse(trimmed);
    if (typeof obj === "object" && obj !== null && !Array.isArray(obj)) {
      return JSON.stringify(redactPayload(obj as Record<string, unknown>));
    }
  } catch {
    // not valid JSON — pass through
  }
  return s;
}
function redactPayload(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (isSensitiveField(key)) {
      result[key] = "[REDACTED]";
    } else if (typeof value === "string") {
      // Handle JSON strings containing sensitive keys (e.g. headers: '{"Authorization":"..."}')
      result[key] = redactJSONString(value);
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      result[key] = redactPayload(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      result[key] = value.map(item => item && typeof item === "object" && !Array.isArray(item) ? redactPayload(item as Record<string, unknown>) : item);
    } else {
      result[key] = value;
    }
  }
  return result;
}
export interface ErrorAnalysisContext {
  nodeType?: string;
  nodeName?: string;
  errorMessage: string;
  nodeInput?: Record<string, unknown>;
  /** Node output/result — often contains the actual API error response */
  nodeOutput?: Record<string, unknown>;
  /** Node configuration/payload (url, method, headers, body, etc.) */
  nodeConfig?: Record<string, unknown>;
  runId?: string;
  workflowId?: string;
  /** Whether the error originated from a test run */
  isTestRun?: boolean;
}
interface UseErrorAnalysisReturn {
  /** AI analysis result (null if not yet analyzed) */
  analysis: ErrorAnalysisResult | null;
  /** Whether AI is currently analyzing */
  isAnalyzing: boolean;
  /** Whether the result came from cache */
  isCached: boolean;
  /** Manually trigger analysis */
  triggerAnalysis: () => void;
  /** Re-analyze, bypassing cache */
  reanalyze: () => void;
}

/** Response shape from POST /v1/ai/workflow/error-analysis */
interface ErrorAnalysisAPIResponse {
  code: number;
  data: {
    analysis: string;
    isFixable: boolean;
    suggestedFix?: string;
    fixableFields?: Array<{
      fieldName: string;
      suggestedValue: string;
      explanation: string;
    }>;
  };
}

/**
 * Infer fixable fields from the analysis/suggestedFix text when the backend
 * doesn't return structured fixableFields. Catches patterns like:
 *   Field 'headers': change to '{"userid": "12345"}' — reason: ...
 *   Field `method`: change to `POST` — reason: ...
 */
function inferFixableFields(analysis: string, suggestedFix: string | null, nodeConfig?: Record<string, unknown>): FixableField[] {
  const fields: FixableField[] = [];
  const seen = new Set<string>();
  const fullText = `${analysis}\n${suggestedFix ?? ""}`;

  // Pattern: Field 'fieldName': change to 'value' — reason: explanation
  // Supports backticks, single quotes, double quotes around field name and value
  const fieldPattern = /Field\s+[`'""](\w+)[`'""]\s*:\s*change\s+to\s+[`'""](.+?)[`'""]\s*(?:[—–-]+\s*reason:\s*(.+?))?$/gim;
  let match;
  while ((match = fieldPattern.exec(fullText)) !== null) {
    const fieldName = match[1];
    if (seen.has(fieldName)) continue;
    seen.add(fieldName);
    let suggestedValue: unknown = match[2];
    const explanation = match[3]?.trim() || "";
    try {
      suggestedValue = JSON.parse(suggestedValue as string);
    } catch {
      // keep as string
    }
    fields.push({
      fieldName,
      currentValue: nodeConfig?.[fieldName],
      suggestedValue,
      explanation
    });
  }
  return fields;
}
export function useErrorAnalysis(context: ErrorAnalysisContext | null): UseErrorAnalysisReturn {
  // Generate cache key for this error context
  const cacheKey = context ? generateCacheKey({
    nodeType: context.nodeType,
    errorMessage: context.errorMessage,
    nodeInput: context.nodeInput,
    nodeConfig: context.nodeConfig
  }) : null;

  // Initialize from cache synchronously to avoid unnecessary API calls
  const initialCached = cacheKey ? getCachedAnalysis(cacheKey) : null;
  const [analysis, setAnalysis] = useState<ErrorAnalysisResult | null>(initialCached);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCached, setIsCached] = useState(!!initialCached);
  const abortRef = useRef<AbortController | null>(null);
  // Track which cacheKey we've already triggered analysis for to prevent re-triggers on remount
  const analyzedKeyRef = useRef<string | null>(initialCached ? cacheKey : null);
  // Store context in ref to keep performAnalysis stable
  const contextRef = useRef(context);
  contextRef.current = context;

  // When cacheKey changes, check cache and reset analysis state.
  // If we already have analysis (e.g. after applying a fix that changed nodeConfig),
  // preserve it under the new key so the UI doesn't flash/reset.
  useEffect(() => {
    if (!cacheKey) {
      setAnalysis(null);
      setIsCached(false);
      return;
    }
    const cached = getCachedAnalysis(cacheKey);
    if (cached) {
      setAnalysis(cached);
      setIsCached(true);
      analyzedKeyRef.current = cacheKey;
    } else {
      setAnalysis(null);
      setIsCached(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);
  const performAnalysis = useCallback(async () => {
    const ctx = contextRef.current;
    if (!ctx || !cacheKey) return;

    // Check cache again
    const cached = getCachedAnalysis(cacheKey);
    if (cached) {
      setAnalysis(cached);
      setIsCached(true);
      analyzedKeyRef.current = cacheKey;
      return;
    }

    // Cancel any in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Timeout after 15 seconds to prevent infinite loading
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 15_000);
    setIsAnalyzing(true);
    setIsCached(false);
    analyzedKeyRef.current = cacheKey;
    try {
      const response = await authenticatedFetch<ErrorAnalysisAPIResponse>(`${V2_ENDPOINT}/v1/ai/workflow/error-analysis`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          nodeType: ctx.nodeType || "unknown",
          nodeName: ctx.nodeName || "Unknown",
          errorMessage: ctx.errorMessage,
          ...(ctx.nodeConfig && Object.keys(ctx.nodeConfig).length > 0 && {
            nodeConfig: redactPayload(ctx.nodeConfig)
          }),
          ...(ctx.nodeInput && Object.keys(ctx.nodeInput).length > 0 && {
            nodeInput: redactPayload(ctx.nodeInput)
          }),
          ...(ctx.nodeOutput && Object.keys(ctx.nodeOutput).length > 0 && {
            nodeOutput: redactPayload(ctx.nodeOutput)
          })
        }),
        signal: controller.signal
      });
      if (!response.ok) {
        throw new Error(`Analysis request failed: ${response.status}`);
      }
      const json: ErrorAnalysisAPIResponse = await response.json();
      clearTimeout(timeoutId);
      const data = json.data;

      // Map fixable fields from API, enriching with current values from node config
      let fixableFields: FixableField[] = (data.fixableFields ?? []).map(f => {
        const currentValue = ctx.nodeConfig?.[f.fieldName];
        // Try to parse JSON suggested values (e.g. '{"userid": "12345"}')
        let suggestedValue: unknown = f.suggestedValue;
        try {
          suggestedValue = JSON.parse(f.suggestedValue);
        } catch {
          // Keep as string
        }
        return {
          fieldName: f.fieldName,
          currentValue,
          suggestedValue,
          explanation: f.explanation
        };
      });

      // Fallback: infer fixable fields from analysis text when backend doesn't return them
      if (fixableFields.length === 0 && data.isFixable) {
        fixableFields = inferFixableFields(data.analysis, data.suggestedFix ?? null, ctx.nodeConfig);
      }
      const result: ErrorAnalysisResult = {
        analysis: data.analysis,
        isFixable: data.isFixable,
        suggestedFix: data.suggestedFix || null,
        fixableFields,
        timestamp: Date.now()
      };

      // Cache the result
      setCachedAnalysis(cacheKey, result);
      if (!controller.signal.aborted) {
        setAnalysis(result);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      // If aborted by a newer request (not timeout), silently bail
      if (err instanceof Error && err.name === "AbortError" && !timedOut) {
        // Still reset loading state so the spinner doesn't get stuck
        setIsAnalyzing(false);
        return;
      }
      console.error("[ErrorAnalysis] AI analysis failed:", err);
      // On failure (including timeout), show fallback and allow retry
      setAnalysis({
        analysis: "Unable to analyze this error automatically. Use 'Fix with Caddie' for a detailed AI conversation about this error.",
        isFixable: false,
        suggestedFix: null,
        fixableFields: [],
        timestamp: Date.now()
      });
    } finally {
      setIsAnalyzing(false);
    }
  }, [cacheKey]);

  // Auto-trigger analysis when a new error appears (different cacheKey than what we've already analyzed)
  useEffect(() => {
    if (cacheKey && cacheKey !== analyzedKeyRef.current) {
      performAnalysis();
    }
    // performAnalysis is stable for a given cacheKey, no need to include it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // Don't abort on unmount — the request has a 15s timeout for cleanup,
  // and aborting on unmount causes premature cancellation when parent
  // components (e.g. Accordion) trigger unmount/remount cycles.
  // The abortRef is only used by performAnalysis to cancel a previous
  // in-flight request when a new one starts.

  const reanalyze = useCallback(() => {
    if (cacheKey) {
      removeCachedAnalysis(cacheKey);
    }
    analyzedKeyRef.current = null;
    setAnalysis(null);
    setIsCached(false);
    performAnalysis();
  }, [cacheKey, performAnalysis]);
  return {
    analysis,
    isAnalyzing,
    isCached,
    triggerAnalysis: performAnalysis,
    reanalyze
  };
}